import { Rect } from '../../graphics/geometry.js';
import { AnimationHelper } from '../animation/animation-handler.js';
import { SizedCanvasElement, CanvasElement } from '../canvas-element.js';
import { TextRenderCanvasMgr, TextRenderCanvas } from '../utils/text-render-canvas.js';
import { LruCache } from '../../utils/lru-cache.js';

class TextElement extends SizedCanvasElement {
    constructor(_text, textStyle, margin, hitable, name, useRenderCanvas = true, useCache = false, adjustHeightToMinimumTextHeight = false) {
        super(hitable, name);
        this._text = _text;
        this.textStyle = textStyle;
        this.margin = margin;
        this.useRenderCanvas = useRenderCanvas;
        this.useCache = useCache;
        this.adjustHeightToMinimumTextHeight = adjustHeightToMinimumTextHeight;
        this.canvasFontSize = { width: 0, height: 0, ascent: 0, fontLeft: 0, fontRight: 0 };
        if (useRenderCanvas && !TextRenderCanvasMgr.polyfillLoaded) {
            this._loadingPromise = TextRenderCanvasMgr.loadMeasureTextPolyfillIfNeeded.then(() => {
                this.calculateSize();
                if (this.parent) {
                    this.invalidate();
                }
            });
        }
        else {
            this.calculateSize();
        }
    }
    getLoadingPromises() {
        return this._loadingPromise ? [this._loadingPromise] : [];
    }
    static releaseRenderCanvas() {
        TextRenderCanvasMgr.map = {};
    }
    /**
     * Calculate the size of the text in pixels
     */
    calculateSize() {
        var _a, _b;
        const can = document.createElement("canvas");
        const ctx = can.getContext("2d");
        const scale = this.getCurrentScale();
        if (!this._text) {
            this._width = 0;
            this._height = 0;
        }
        else {
            if (ctx) {
                if (this.useRenderCanvas) {
                    this.renderCanvas = TextRenderCanvasMgr.getRenderCanvas(this.textStyle, (_a = this.board) === null || _a === void 0 ? void 0 : _a.transform, this.adjustHeightToMinimumTextHeight);
                    this.canvasFontSize = this.renderCanvas.calculate(this._text);
                }
                else {
                    this.textStyle.prepareCanvasContextForText(ctx, (_b = this.board) === null || _b === void 0 ? void 0 : _b.transform, true);
                    this.canvasFontSize = TextRenderCanvas.getFontSize(ctx, this._text);
                }
                this._width = Math.ceil(this.canvasFontSize.width / scale);
                this._height = Math.ceil(this.canvasFontSize.height / scale);
            }
            else {
                this._width = (this._height / 2) * this._text.length;
                this._height = Math.ceil(this.textStyle.size);
                this.canvasFontSize.width = this.applyScale(this._width, "ceil");
                this.canvasFontSize.height = this.applyScale(this._height, "ceil");
                this.canvasFontSize.ascent = 0;
            }
        }
        this._width += this.margin * 2;
        this._height += this.margin * 2;
        return scale;
    }
    getCurrentScale() {
        var _a, _b;
        return this.floating ? 1 : (_b = (_a = this.board) === null || _a === void 0 ? void 0 : _a.transform.scale) !== null && _b !== void 0 ? _b : 1;
    }
    setParent(parent, board) {
        super.setParent(parent, board);
        // Recalculate the size (because of the scaling)
        this.rescaled();
        // Invalidate with the new size
        this.invalidate();
    }
    /**
     * Update the text style with the new values
     * @param newValues new values to apply
     */
    updateStyle(newValues) {
        var _a;
        if (!newValues || typeof newValues != "object") {
            return false;
        }
        this.beforeUpdate();
        if ((_a = AnimationHelper.copyMatchingKeyValues(newValues, this.textStyle)) === null || _a === void 0 ? void 0 : _a.length) {
            this.rescaled();
            this.afterUpdate();
            return true;
        }
        return false;
    }
    rescaled() {
        var _a;
        this.calculateSize();
        if (this.useRenderCanvas) {
            this.renderCanvas = TextRenderCanvasMgr.getRenderCanvas(this.textStyle, (_a = this.board) === null || _a === void 0 ? void 0 : _a.transform, this.adjustHeightToMinimumTextHeight);
            this.canvasFontSize = this.renderCanvas.calculate(this._text);
            const scale = this.getCurrentScale();
            this._width = this.canvasFontSize.width / scale + this.margin * 2;
            this._height = this.canvasFontSize.height / scale + this.margin * 2;
        }
    }
    /**
     * Draw the text on the context
     */
    doDraw(ctx /*, clipRect?: Rect*/) {
        var _a, _b, _c, _d;
        const r = this.getRect();
        let x = this.applyTransformX(r.x, true);
        let y = this.applyTransformY(r.y, true);
        const scaledWidth = this.applyScale(r.width, "ceil");
        const scaledHeight = this.applyScale(r.height, "ceil");
        if (this.textStyle.backgroundColor) {
            ctx.fillStyle = this.textStyle.backgroundColor.tohexString();
            ctx.fillRect(x, y, scaledWidth, scaledHeight);
        }
        if (CanvasElement._DEBUG_) {
            console.log('Drawing text:', this._text, 'at', x, y, 'with size', scaledWidth, 'x', scaledHeight, 'and style', this.textStyle.getHashCode());
        }
        if (this.useRenderCanvas) {
            if (!this.renderCanvas) {
                this.renderCanvas = TextRenderCanvasMgr.getRenderCanvas(this.textStyle, (_a = this.board) === null || _a === void 0 ? void 0 : _a.transform, this.adjustHeightToMinimumTextHeight);
                this.canvasFontSize = this.renderCanvas.calculate(this._text);
            }
            x += Math.round((scaledWidth - this.canvasFontSize.width) / 2);
            y += Math.round((scaledHeight - this.canvasFontSize.height) / 2);
            if (this.renderCanvas.textStyle.strokeStyle) {
                x += this.renderCanvas.textStyle.lineWidth;
                y += this.renderCanvas.textStyle.lineWidth;
            }
            this.renderCanvas.drawText(ctx, x, y, this._text);
        }
        else if (this.useCache) {
            const cacheKey = this.textStyle.getHashCode() + "," + this._text + "," + ((_d = (_c = (_b = this.board) === null || _b === void 0 ? void 0 : _b.transform) === null || _c === void 0 ? void 0 : _c._scale) !== null && _d !== void 0 ? _d : 1);
            let cachedCanvas = TextElement.textImageCache.get(cacheKey);
            if (!cachedCanvas) {
                cachedCanvas = this.createCachedCanvas(cacheKey);
            }
            ctx.drawImage(cachedCanvas, 0, 0, cachedCanvas.width, cachedCanvas.height, x, y, cachedCanvas.width, cachedCanvas.height);
        }
        else {
            this.drawOnCanvas(ctx, x, y);
        }
        return true;
    }
    createCachedCanvas(cacheKey) {
        var _a;
        const canvas = document.createElement("canvas");
        const scaledWidth = this.applyScale(this._width, "ceil");
        const scaledHeight = this.applyScale(this._height, "ceil");
        canvas.width = scaledWidth;
        canvas.height = scaledHeight;
        const cachedCanvasCtx = canvas.getContext("2d");
        this.textStyle.prepareCanvasContext(cachedCanvasCtx, (_a = this.board) === null || _a === void 0 ? void 0 : _a.transform);
        this.drawOnCanvas(cachedCanvasCtx, 0, 0, false);
        TextElement.textImageCache.put(cacheKey, canvas);
        return canvas;
    }
    drawOnCanvas(ctx, left, top, center = true) {
        var _a, _b;
        (_a = this.textStyle) === null || _a === void 0 ? void 0 : _a.prepareCanvasContextForText(ctx, (_b = this.board) === null || _b === void 0 ? void 0 : _b.transform, true);
        if (this.canvasFontSize.ascent > 0) {
            ctx.textBaseline = "alphabetic";
        }
        else {
            ctx.textBaseline = "top";
        }
        const marginInCanvasCoord = this.applyScale(this.margin, "round");
        ctx.textAlign = center ? "center" : "left";
        const scaledWidth = this.applyScale(this._width, "");
        const scaledHeight = this.applyScale(this._height, "");
        const x = center ? left + Math.round(scaledWidth / 2) : left + marginInCanvasCoord;
        const y = this.canvasFontSize.ascent > 0 ? top + marginInCanvasCoord + this.canvasFontSize.ascent : top + marginInCanvasCoord;
        if (this.textStyle.strokeStyle && this.textStyle.strokeBehind) {
            ctx.strokeText(this._text, x, y);
        }
        ctx.fillText(this._text, x, y);
        if (this.textStyle.strokeStyle && !this.textStyle.strokeBehind) {
            ctx.strokeText(this._text, x, y);
        }
        return new Rect(left + marginInCanvasCoord, top + marginInCanvasCoord, scaledWidth - marginInCanvasCoord * 2, scaledHeight - +marginInCanvasCoord * 2);
    }
    get text() {
        return this._text;
    }
    set text(value) {
        if (value != this._text) {
            this.invalidate();
            this._text = value;
            this.rescaled();
            this.invalidate();
        }
    }
}
TextElement.textImageCache = new LruCache(1000);

export { TextElement };
//# sourceMappingURL=text-element.js.map
